import { PROFILE_MAX_VISIBLE_SERVICES } from '@utils/constants';
import { Box, Button, PopoverMenu, Tag, Text } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { FC, useMemo, useState } from 'react';
import { useProfileContext } from '../../Contexts/ProfileContext';
import s from './Services.module.scss';
import { useHasServices } from './useHasServices';

const Services: FC<{ isMobileView?: boolean }> = ({ isMobileView }) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState(false);
  const { partnerProfile, services = [] } = useProfileContext();
  const hasServices = useHasServices();

  const foundServices = useMemo(() => {
    const servicesList = services.flatMap((category) => category.services);
    return (partnerProfile.services?.values || [])
      .map(({ id }) => ({
        key: id,
        dataHook: `tag-service-${id}`,
        content: servicesList.find((service) => service?.id === id)?.displayKey,
      }))
      .filter((item) => item.content);
  }, [partnerProfile.services?.values, services]);

  const visibleServices =
    showAll && isMobileView
      ? foundServices
      : foundServices.slice(0, PROFILE_MAX_VISIBLE_SERVICES);
  const remainingServices = foundServices.slice(PROFILE_MAX_VISIBLE_SERVICES);
  const hasMoreServices = foundServices.length > PROFILE_MAX_VISIBLE_SERVICES;

  if (!hasServices) {
    return null;
  }

  const handleShowAllServices = () => {
    setShowAll((prev) => !prev);
  };

  const handleShowLessServices = () => {
    setShowAll(false);
  };

  const handleShowMoreClick = () => {
    handleShowAllServices();
  };

  const moreButton = (
    <Button
      className={s.showButton}
      dataHook="tag-more-count"
      key="show-more-button"
      size="tiny"
      priority="secondary"
      skin="dark"
      onClick={handleShowMoreClick}
    >
      {t('profile-page-services-more-count-label', {
        moreCount: foundServices.length - PROFILE_MAX_VISIBLE_SERVICES,
      })}
    </Button>
  );

  const showLessButton = (
    <Button
      className={s.showButton}
      key="show-less-button"
      dataHook="tag-show-less"
      size="tiny"
      priority="secondary"
      skin="dark"
      onClick={handleShowLessServices}
    >
      {t('profile-page-services-show-less-label')}
    </Button>
  );

  return (
    <Box
      direction="vertical"
      className={`${s.services} ${isMobileView ? s.mobile : ''}`}
    >
      <Text size="small">{t('profile-page-services')}</Text>
      <Box marginTop="12px" dataHook="services-tags">
        <div className={s.root}>
          {visibleServices.map((tag) => (
            <Tag
              className={s.tag}
              key={tag.key}
              dataHook={tag.dataHook}
              id="standard"
              removable={false}
              skin="lightOutlined"
            >
              {t(`explore.services.${tag.content}`)}
            </Tag>
          ))}
          {hasMoreServices && (
            <>
              {isMobileView ? (
                <>{showAll ? showLessButton : moreButton}</>
              ) : (
                !showAll && (
                  <PopoverMenu
                    className={s.popoverMenu}
                    appendTo="window"
                    placement="left"
                    minWidth={180}
                    maxWidth={407}
                    triggerElement={moreButton}
                  >
                    <div className={s.popoverContent} role="presentation">
                      {remainingServices.map((service) => (
                        <Tag
                          className={s.tag}
                          key={service.key}
                          dataHook={service.dataHook}
                          id="standard"
                          removable={false}
                          skin="lightOutlined"
                        >
                          {t(`explore.services.${service.content}`)}
                        </Tag>
                      ))}
                    </div>
                  </PopoverMenu>
                )
              )}
            </>
          )}
        </div>
      </Box>
    </Box>
  );
};

export default Services;
